import { useState } from 'react';
import { createQuizQuestion, updateQuizQuestion } from '../../services/content/quizQuestionService';
import type { QuizQuestion, QuizQuestionOption } from '../../types/content/quizQuestion';

type View = 'main' | 'optionDetail';

interface QuizQuestionModalProps {
  courseId: string;
  question?: QuizQuestion;
  nextNumber: number;
  onClose: () => void;
  onSuccess: () => void;
}

export function QuizQuestionModal({ courseId, question, nextNumber, onClose, onSuccess }: QuizQuestionModalProps) {
  const isEdit = !!question;

  const [number, setNumber] = useState<number>(question?.number ?? nextNumber);
  const [type, setType] = useState(question?.type || 'multiple_choice');
  const [text, setText] = useState(question?.text || '');
  const [correctOption, setCorrectOption] = useState(question?.correctOption || '');
  const [options, setOptions] = useState<QuizQuestionOption[]>(
    question?.options ? JSON.parse(JSON.stringify(question.options)) : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Option detail state
  const [view, setView] = useState<View>('main');
  const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
  const [optLetter, setOptLetter] = useState('');
  const [optAnswer, setOptAnswer] = useState('');
  const [optExplanation, setOptExplanation] = useState('');

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm';
  const labelClass = 'block text-sm font-medium text-gray-700 mb-1';
  const btnOutline = 'px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors';
  const btnPrimary = 'px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';
  const btnSmall = 'text-xs px-2 py-1 rounded transition-colors';

  function openOptionForm(index: number | null) {
    if (index !== null) {
      const opt = options[index];
      setOptLetter(opt.option);
      setOptAnswer(opt.answer);
      setOptExplanation(opt.explanation);
    } else {
      // Auto-assign next letter
      const letters = ['A', 'B', 'C', 'D', 'E', 'F'];
      const usedLetters = options.map((o) => o.option);
      const nextLetter = letters.find((l) => !usedLetters.includes(l)) || '';
      setOptLetter(nextLetter);
      setOptAnswer('');
      setOptExplanation('');
    }
    setEditingOptionIndex(index);
    setView('optionDetail');
  }

  function saveOption() {
    if (!optLetter.trim() || !optAnswer.trim()) return;
    const opt: QuizQuestionOption = {
      option: optLetter.trim(),
      answer: optAnswer.trim(),
      explanation: optExplanation.trim(),
    };
    const updated = [...options];
    if (editingOptionIndex !== null) {
      updated[editingOptionIndex] = opt;
    } else {
      updated.push(opt);
    }
    setOptions(updated);
    setView('main');
  }

  function deleteOption(index: number) {
    setOptions(options.filter((_, i) => i !== index));
  }

  async function handleSave() {
    if (!text.trim()) return;
    setError('');
    setLoading(true);
    try {
      const data = {
        courseId,
        number,
        type: type.trim(),
        text: text.trim(),
        correctOption: correctOption.trim(),
        options,
      };
      if (isEdit) {
        await updateQuizQuestion(question!.id, { ...question!, ...data });
      } else {
        await createQuizQuestion(data);
      }
      onSuccess();
    } catch {
      setError(isEdit ? 'Failed to update question' : 'Failed to create question');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">

        {view === 'main' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {isEdit ? 'Edit Quiz Question' : 'Add Quiz Question'}
              </h2>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Question #</label>
                    <input type="number" value={number} onChange={(e) => setNumber(parseInt(e.target.value, 10) || 1)} className={inputClass} min={1} />
                  </div>
                  <div>
                    <label className={labelClass}>Type</label>
                    <input type="text" value={type} onChange={(e) => setType(e.target.value)} className={inputClass} placeholder="multiple_choice" />
                  </div>
                </div>
                <div>
                  <label className={labelClass}>Question Text *</label>
                  <textarea value={text} onChange={(e) => setText(e.target.value)} className={inputClass} rows={3} placeholder="Enter the question..." />
                </div>
                <div>
                  <label className={labelClass}>Correct Option</label>
                  <select value={correctOption} onChange={(e) => setCorrectOption(e.target.value)} className={inputClass}>
                    <option value="">Select...</option>
                    {options.map((opt) => (
                      <option key={opt.option} value={opt.option}>{opt.option}</option>
                    ))}
                  </select>
                </div>

                {/* Options */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className={labelClass}>Options ({options.length})</label>
                    <button type="button" onClick={() => openOptionForm(null)} className={`${btnSmall} text-blue-600 hover:bg-blue-50`}>+ Add</button>
                  </div>
                  {options.length === 0 ? (
                    <p className="text-sm text-gray-400">No options yet</p>
                  ) : (
                    <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {options.map((opt, i) => (
                        <div key={i} className="flex items-center justify-between px-3 py-2">
                          <div className="min-w-0">
                            <span className={`font-medium text-sm ${opt.option === correctOption ? 'text-green-700' : 'text-gray-700'}`}>
                              {opt.option}.
                            </span>
                            <span className="text-sm text-gray-600 ml-1 truncate">{opt.answer}</span>
                          </div>
                          <div className="flex items-center gap-1 ml-2 flex-shrink-0">
                            <button type="button" onClick={() => openOptionForm(i)} className={`${btnSmall} text-gray-500 hover:bg-gray-100`}>Edit</button>
                            <button type="button" onClick={() => deleteOption(i)} className={`${btnSmall} text-red-500 hover:bg-red-50`}>Delete</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {error && <p className="text-red-600 text-sm mt-3">{error}</p>}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={onClose} className={btnOutline}>Cancel</button>
              <button type="button" onClick={handleSave} disabled={loading || !text.trim()} className={btnPrimary}>
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </>
        )}

        {view === 'optionDetail' && (
          <>
            <div className="px-6 pt-6 pb-4 overflow-y-auto flex-1">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {editingOptionIndex !== null ? 'Edit Option' : 'Add Option'}
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Option Letter *</label>
                  <input type="text" value={optLetter} onChange={(e) => setOptLetter(e.target.value.toUpperCase())} className={inputClass} placeholder="A" maxLength={1} />
                </div>
                <div>
                  <label className={labelClass}>Answer Text *</label>
                  <textarea value={optAnswer} onChange={(e) => setOptAnswer(e.target.value)} className={inputClass} rows={2} placeholder="Answer text" />
                </div>
                <div>
                  <label className={labelClass}>Explanation</label>
                  <textarea value={optExplanation} onChange={(e) => setOptExplanation(e.target.value)} className={inputClass} rows={2} placeholder="Why this is correct/incorrect" />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button type="button" onClick={() => setView('main')} className={btnOutline}>Back</button>
              <button type="button" onClick={saveOption} disabled={!optLetter.trim() || !optAnswer.trim()} className={btnPrimary}>Done</button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
